import { useState } from "react";
import { toast, Toaster } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { BRAND } from "@/data/products";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    const num = (BRAND.whatsapp || "").replace(/[^0-9]/g, "");
    const lines = [
      `Hi ${BRAND.name}, I have an enquiry.`,
      "",
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.subject ? `Subject: ${form.subject}` : null,
      "",
      form.message,
    ].filter((l) => l !== null);
    const url = `https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — just hit send to reach us.");
    setForm({ name: "", email: "", subject: "", message: "" });
    setLoading(false);
  };

  const inputClass =
    "w-full bg-transparent border-b border-ink/20 py-3.5 mt-2 font-primary text-ink placeholder-ink/30 focus:outline-none focus:border-coral transition-colors";

  return (
    <div data-testid="contact-page" className="bg-cream min-h-screen">
      <Toaster position="top-center" theme="light" richColors />

      <section className="relative overflow-hidden px-6 sm:px-12 lg:px-24 pt-36 sm:pt-40 pb-24 sm:pb-32">
        <div className="absolute inset-0 pattern-bg opacity-50 pointer-events-none" />
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-16 lg:gap-24">
          {/* Left — intro + details */}
          <div className="md:col-span-5">
            <p data-testid="contact-eyebrow" className="eyebrow animate-fade-in-up">
              Get in Touch
            </p>
            <h1
              data-testid="contact-title"
              className="font-display text-5xl sm:text-6xl font-bold text-ink mt-6 leading-[1] animate-fade-in-up delay-100"
            >
              Let's talk <span className="text-coral">spice.</span>
            </h1>
            <p className="font-primary text-lg text-ink/60 mt-7 max-w-md leading-relaxed animate-fade-in-up delay-200">
              Wholesale, samples, or simply a question — write to us and a real
              person will reply.
            </p>

            <div className="mt-14 flex flex-col gap-8">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="flex items-start gap-4 group"
              >
                <Mail size={20} className="text-coral mt-1" strokeWidth={1.8} />
                <div>
                  <p className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-ink/40">Email</p>
                  <p className="font-primary text-ink group-hover:text-coral transition-colors mt-1">{BRAND.email}</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <Phone size={20} className="text-coral mt-1" strokeWidth={1.8} />
                <div>
                  <p className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-ink/40">Phone</p>
                  <p className="font-primary text-ink mt-1">{BRAND.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-coral mt-1" strokeWidth={1.8} />
                <div>
                  <p className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-ink/40">Studio</p>
                  <p className="font-primary text-ink mt-1">{BRAND.city}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="md:col-span-6 md:col-start-7">
            <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-9">
              <div>
                <label className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">Name</label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">Email</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">Subject</label>
                <input
                  data-testid="contact-input-subject"
                  type="text"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="Wholesale enquiry, samples…"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="font-primary text-xs font-semibold uppercase tracking-[0.18em] text-ink/50">Message</label>
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us what you're after…"
                  className={`${inputClass} resize-none`}
                />
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="btn-primary group self-start disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
